// Ephemeral presence for the doc viewer (CONTRACTS §6b). Backed by Firebase
// Realtime Database so onDisconnect() drops a user the instant their tab closes
// or crashes. Presence is NOT content — it lives only in RTDB, gated by
// database.rules.json (own-uid write, @cellery.ai read). All no-ops if RTDB is
// unconfigured (rtdb === null) so the rest of the app degrades gracefully.

import {
  ref,
  onValue,
  onDisconnect,
  set,
  serverTimestamp,
} from "firebase/database";
import { rtdb } from "./firebase";

export type PresenceMode = "view" | "edit";

export interface PresentUser {
  uid: string;
  email: string;
  name: string;
  mode: PresenceMode;
  lastChanged: number;
}

export interface PresenceHandle {
  setMode: (mode: PresenceMode) => void;
  leave: () => void;
}

const NOOP: PresenceHandle = { setMode: () => {}, leave: () => {} };

/**
 * Announce this user's presence on a doc and keep it live. Re-arms the
 * onDisconnect auto-remove on every (re)connect. Returns a handle to update the
 * mode (view/edit) or leave explicitly.
 */
export function joinPresence(
  docId: string,
  uid: string,
  email: string,
  name: string,
  initialMode: PresenceMode
): PresenceHandle {
  if (!rtdb) return NOOP;
  const meRef = ref(rtdb, `status/${docId}/${uid}`);
  const connRef = ref(rtdb, ".info/connected");
  let mode: PresenceMode = initialMode;

  const write = () =>
    set(meRef, { email, name, mode, lastChanged: serverTimestamp() }).catch(
      () => {
        /* presence is best-effort */
      }
    );

  const unsubConn = onValue(connRef, (snap) => {
    if (snap.val() !== true) return;
    onDisconnect(meRef)
      .remove()
      .then(write)
      .catch(() => {});
  });

  return {
    setMode(next: PresenceMode) {
      mode = next;
      void write();
    },
    leave() {
      unsubConn();
      void set(meRef, null).catch(() => {});
    },
  };
}

/** Subscribe to everyone present on a doc. Returns an unsubscribe function. */
export function watchPresence(
  docId: string,
  cb: (users: PresentUser[]) => void
): () => void {
  if (!rtdb) {
    cb([]);
    return () => {};
  }
  const colRef = ref(rtdb, `status/${docId}`);
  return onValue(colRef, (snap) => {
    const val =
      (snap.val() as Record<string, Omit<PresentUser, "uid">> | null) ?? {};
    cb(
      Object.entries(val).map(([uid, v]) => ({
        uid,
        email: v.email,
        name: v.name,
        mode: v.mode === "edit" ? "edit" : "view",
        lastChanged: typeof v.lastChanged === "number" ? v.lastChanged : 0,
      }))
    );
  });
}
